import { useMemo } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../../_metronic/layout/core'
import { CustomTabs, InfoAlert, TabItem } from '../../../components'
import { useT } from '_metronic/i18n/Metronici18n'
import { useAuth } from '../../auth'
import { acessoEscala } from './core/escalaHelpers'
import { usePermissoesFeriado } from './core/usePermissoesFeriado'
import { EscalaEmpresaWrapper } from './escala-empresa/EscalaEmpresa'
import { FeriadosListWrapper } from './feriados-list/FeriadosList'
import { MinhaEscalaWrapper } from './minha-escala/MinhaEscala'

type AbaEscala = 'minha-escala' | 'empresa' | 'feriados'

const TITULOS: Record<AbaEscala, string> = {
  'minha-escala': 'Minha escala',
  empresa: 'Escala da instituição',
  feriados: 'Feriados',
}

/**
 * Área de escala: a agenda do próprio profissional, a agenda administrada pela
 * empresa e os feriados, nas mesmas abas — o critério de aceite pede feriados
 * "visíveis e administráveis na mesma área".
 */
const EscalasPage = () => {
  const { t } = useT()
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // `Feriado:read` não é universal: SUPER_MEDICO não o tem, e sem ele a aba
  // abriria só para o `GET /feriados` voltar 403.
  const { podeLer: podeVerFeriados } = usePermissoesFeriado()

  const { eixoPessoal: podeVerEixoPessoal, eixoEmpresa: podeVerEixoEmpresa } = useMemo(
    () => acessoEscala(currentUser),
    [currentUser]
  )

  const abas: TabItem<AbaEscala>[] = useMemo(() => {
    const itens: TabItem<AbaEscala>[] = []
    if (podeVerEixoPessoal) itens.push({ id: 'minha-escala', label: TITULOS['minha-escala'] })
    if (podeVerEixoEmpresa) itens.push({ id: 'empresa', label: TITULOS.empresa })
    if (podeVerFeriados) itens.push({ id: 'feriados', label: TITULOS.feriados })
    return itens
  }, [podeVerEixoEmpresa, podeVerEixoPessoal, podeVerFeriados])

  const primeiraAba = abas[0]?.id
  const abaAtual = (abas.find((aba) => pathname.endsWith(aba.id))?.id ?? primeiraAba) as AbaEscala

  /** Para onde mandar quem cai numa aba que o perfil dele não alcança. */
  const semAcesso = (
    <InfoAlert variant='info' title='Sem acesso à escala'>
      Seu perfil não tem acesso a nenhuma das áreas de escala. Fale com o administrador da sua
      empresa se precisar montar agenda ou consultar feriados.
    </InfoAlert>
  )

  const redirecionaParaPrimeiraAba = (atual: AbaEscala) =>
    primeiraAba && primeiraAba !== atual ? (
      <Navigate to={`/apps/escalas/${primeiraAba}`} replace />
    ) : (
      semAcesso
    )

  const navegacao = (
    <CustomTabs<AbaEscala>
      tabs={abas}
      activeTab={abaAtual}
      onChange={(aba) => navigate(`/apps/escalas/${aba}`)}
      className='mb-6'
    />
  )

  return (
    <Routes>
      <Route
        path='minha-escala'
        element={
          <>
            <PageTitle description={t('pagina.escalas.descricao')}>Escalas</PageTitle>
            {navegacao}
            {podeVerEixoPessoal ? (
              <MinhaEscalaWrapper />
            ) : (
              redirecionaParaPrimeiraAba('minha-escala')
            )}
          </>
        }
      />
      <Route
        path='empresa'
        element={
          <>
            <PageTitle description={t('pagina.escalas.descricao')}>Escalas</PageTitle>
            {navegacao}
            {podeVerEixoEmpresa ? <EscalaEmpresaWrapper /> : redirecionaParaPrimeiraAba('empresa')}
          </>
        }
      />
      <Route
        path='feriados'
        element={
          <>
            <PageTitle description={t('pagina.escalas.descricao')}>Escalas</PageTitle>
            {navegacao}
            {podeVerFeriados ? <FeriadosListWrapper /> : redirecionaParaPrimeiraAba('feriados')}
          </>
        }
      />
      <Route
        index
        element={
          primeiraAba ? (
            <Navigate to={`/apps/escalas/${primeiraAba}`} replace />
          ) : (
            <>
              <PageTitle description={t('pagina.escalas.descricao')}>Escalas</PageTitle>
              {semAcesso}
            </>
          )
        }
      />
    </Routes>
  )
}

export default EscalasPage
